import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { createStore, getStores, updateStore } from '../api/api';
import { Store, Plus, Edit2 } from 'lucide-react';

const Stores = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    currency: 'Q',
    initial_cash_flow: 500,
    petty_cash_fund: 0
  });
  const [editData, setEditData] = useState({
    name: '',
    currency: 'Q',
    petty_cash_assigned: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadStores = useCallback(async () => {
    try {
      const data = await getStores();
      setStores(data);
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await createStore({
        ...formData,
        initial_cash_flow: parseFloat(formData.initial_cash_flow),
        petty_cash_fund: parseFloat(formData.petty_cash_fund)
      });
      setSuccess('¡Tienda creada exitosamente!');
      setFormData({ name: '', currency: 'Q', initial_cash_flow: 500, petty_cash_fund: 0 });
      setShowForm(false);
      loadStores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear la tienda');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (store) => {
    setSelectedStore(store);
    setEditData({
      name: store.name,
      currency: store.currency,
      petty_cash_assigned: store.petty_cash_assigned || 0
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await updateStore(selectedStore.id, editData);
      setSuccess('¡Tienda actualizada exitosamente!');
      setShowEditModal(false);
      setSelectedStore(null);
      loadStores();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al actualizar la tienda');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <p className="text-red-800 text-lg">No tienes permisos para acceder a esta sección</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header futurista */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent flex items-center space-x-3" data-testid="stores-title">
            <Store size={40} className="text-orange-600" />
            <span>Gestión de Tiendas</span>
          </h1>
          <p className="text-gray-600 mt-2 ml-1">Administrar tiendas del sistema</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center space-x-2 font-semibold"
          data-testid="add-store-button"
        >
          <Plus size={22} />
          <span>Nueva Tienda</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center space-x-3" data-testid="stores-error">
          <span className="text-2xl">⚠️</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center space-x-3 animate-pulse" data-testid="stores-success">
          <span className="text-2xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Crear Nueva Tienda</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Tienda
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ej: Siempre Ciudad Nueva"
                  required
                  data-testid="store-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moneda
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="currency-select"
                >
                  <option value="Q">Q - Quetzal (Guatemala)</option>
                  <option value="$">$ - Dólar (USA)</option>
                  <option value="€">€ - Euro</option>
                  <option value="MXN">MXN - Peso Mexicano</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flujo de Caja Inicial
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.initial_cash_flow}
                  onChange={(e) => setFormData({ ...formData, initial_cash_flow: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="initial-cash-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fondo de Sencillo Inicial
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.petty_cash_fund}
                  onChange={(e) => setFormData({ ...formData, petty_cash_fund: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="petty-cash-input"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                data-testid="submit-store-button"
              >
                {submitting ? 'Creando...' : 'Crear Tienda'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Tiendas Registradas</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-600">Cargando...</div>
        ) : stores.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No hay tiendas registradas</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {stores.map((store) => (
              <div key={store.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow" data-testid={`store-card-${store.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <Store className="text-blue-600" size={24} />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{store.name}</h3>
                      <p className="text-xs text-gray-500">ID: {store.id.slice(0, 8)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(store)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Editar tienda"
                  >
                    <Edit2 size={18} />
                  </button>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Moneda:</span>
                    <span className="text-sm font-semibold text-gray-900">{store.currency}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Saldo Actual:</span>
                    <span className="text-sm font-semibold text-green-600">{store.currency}{store.current_balance.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Fondo Sencillo Actual:</span>
                    <span className="text-sm font-semibold text-blue-600">{store.currency}{store.petty_cash_fund.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Fondo Sencillo Asignado:</span>
                    <span className="text-sm font-semibold text-purple-600">{store.currency}{(store.petty_cash_assigned || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Flujo Inicial:</span>
                    <span className="text-sm font-semibold text-gray-600">{store.currency}{store.initial_cash_flow.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      store.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {store.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Edición */}
      {showEditModal && selectedStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Editar Tienda: {selectedStore.name}</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Tienda
                </label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moneda
                </label>
                <select
                  value={editData.currency}
                  onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Q">Q - Quetzal (Guatemala)</option>
                  <option value="$">$ - Dólar (USA)</option>
                  <option value="€">€ - Euro</option>
                  <option value="MXN">MXN - Peso Mexicano</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fondo de Sencillo Asignado
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editData.petty_cash_assigned}
                  onChange={(e) => setEditData({ ...editData, petty_cash_assigned: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Este es el monto fijo que deben tener en el fondo de sencillo. Los conteos deben cuadrar con este valor.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ <strong>Nota:</strong> Los saldos de caja no se pueden modificar por seguridad. Solo el fondo de sencillo asignado.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? 'Guardando...' : 'Guardar Cambios'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedStore(null);
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stores;
